import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileArrowUp } from "@fortawesome/free-solid-svg-icons";
import { NavLink, useLocation, useMatch, useNavigate } from "react-router-dom";
import { useContext, useEffect } from "react";
import ProductContext, {
  ProductContextInterface,
} from "../../context/ProductContext";
import AuthContext from "../../context/AuthContext";
import { ArtworkMasterMenu } from "../../routes/master-artwork/components/menu-items/ArtworkMasterMenu";
import { ArtworkMenu } from "../../routes/artwork/menu-items/ArtworkMenu";
import ButtonIcon from "../ButtonIcon";
import { VersionHud } from "./VersionHud";

//SIDE MENU
function SideNav({ showMenu, toggle }: { showMenu: boolean; toggle: any }) {
  const authCtx = useContext(AuthContext);
  const hasPermission = authCtx?.hasPermission;
  const { productDetails, unsavedChanges, setUnsavedChanges } =
    useContext<ProductContextInterface>(ProductContext);
  const location = useLocation();

  const navigationPaths = {
    moldappLogin: `${process.env.REACT_APP_MOLD_APP_URL}/ords/moldapp/apppack.welcome`,
    moldAppMainMenu: `${
      process.env.REACT_APP_MOLD_APP_URL
    }/ords/moldapp/apppack.mainmenu`,
    similarItems: `product-information/similar-items?p_fg_id=${productDetails?.productId}`,
    buyersRelatedData: `product-information/buyers-data?p_prodid=${productDetails?.productId}&p_proddesc=${productDetails?.description}&p_is_iframe=Y`,
    productMasterSearch: `/product-master`,
    globalProductHierarchy: `${process.env.REACT_APP_GPH_APP_URL}/`,
    productMassUpdate: `${process.env.REACT_APP_PMU_APP_URL}/`,
    packagingProcedures: `${process.env.REACT_APP_PACK_PROCEDURES_APP_URL}/packaging_procedures`,
    bulksSearchTool: `${
      process.env.REACT_APP_MOLD_APP_URL
    }/ords/moldapp/product_master.ask_for_bulks`,
    createNewPis: `skus/create?should_create=true`,
    createNewMpn: `mpn/create?should_create=true`,
    searchPh: `/placeholder/search`,
    searchIn: `/itemnumber/search`,
    pisRequestsList: `skus/requests`,
    mpnRequestsList: `mpn/requests`,
    mpnIntegrations: `mpn/dashboard`,
    searchTcc: `/tccs/search`,
    searchTccRequest: `/tccs/requests`,
    createNewTccRequest: `/tccs/create?should_create=true`,
    tccRequestApprovals: `/tccs/approvals`,
    artworkMaster: "artwork-master",
    artwork: "artwork",
  };

  const navigate = useNavigate();

  const isNoAccessPage = useMatch("/no-access");

  const isProdInfoPage = useMatch("/product-information");
  const isProdInfoRelatedPages = useMatch("/product-information/*");

  const matchSearchPh = useMatch("/placeholder/search");
  const matchViewPH = useMatch("/placeholder/view");

  const matchSearchIn = useMatch("/itemnumber/search");
  const matchViewIN = useMatch("/itemnumber/view");

  const matchSearch = useMatch("/tccs/search");
  const matchView = useMatch("/tccs/view");

  const matchRequest = useMatch("/tccs/requests");
  const matchCreate = useMatch("/tccs/create/");

  const hasRequestId = new URLSearchParams(location.search).has("request_id");
  const hasReadOnly = new URLSearchParams(location.search).has("read_only");
  const hasEdit = new URLSearchParams(location.search).has("is_edit");

  const matchCreateWithoutViewRequest = matchCreate && !hasReadOnly && !hasEdit;

  const matchViewRequest = matchCreate && hasRequestId && hasReadOnly;

  const isSearchPHPage =
    matchSearchPh || matchViewPH ? "collapse-item active" : "collapse-item";

  const isSearchINPage =
    matchSearchIn || matchViewIN ? "collapse-item active" : "collapse-item";

  const isSearchTCCPage =
    matchSearch || matchView ? "collapse-item active" : "collapse-item";

  const isSearchTCCRequestPage =
    matchRequest || matchViewRequest ? "collapse-item active" : "collapse-item";

  const isCreateTCCRequestPage = matchCreateWithoutViewRequest
    ? "collapse-item active"
    : "collapse-item";

  const isMyTCCApprovalsPage = useMatch("/tccs/approvals")
    ? "collapse-item active"
    : "collapse-item";

  const prodInfoPageActiveLink = isProdInfoRelatedPages
    ? "nav-item active"
    : "nav-item";

  const prodMasterSearchActiveLink = useMatch("/product-master")
    ? "nav-item active"
    : "nav-item";

  useEffect(() => {
    if (isProdInfoPage && !showMenu) {
      toggle();
    }
  }, [isProdInfoPage]);

  // To be used when we want to advertise the user for unsaved changes before navigating away
  const handleUnsavedChangesNavigation = (
    event: any,
    pathToNavigate: string,
    shouldCompareCurrentPath?: boolean,
  ) => {
    event.preventDefault();
    const isNavigationToSamePath: boolean =
      location.pathname === pathToNavigate.split("?")[0];

    if (unsavedChanges?.existsUnsavedChanges) {
      const confirmationToLeave = window.confirm(unsavedChanges?.message);
      if (confirmationToLeave) {
        setUnsavedChanges({ existsUnsavedChanges: false });
        navigate(pathToNavigate);
        if (shouldCompareCurrentPath && isNavigationToSamePath) navigate(0);
      }
    } else {
      navigate(pathToNavigate);
      if (shouldCompareCurrentPath && isNavigationToSamePath) navigate(0);
    }
  };

  return (
    <ul
      className={`navbar-nav bg-gradient-primary sidebar sidebar-dark accordion position-relative ${
        showMenu ? "" : "toggled"
      }`}
      id="accordionSidebar"
    >
      {/* Home Page Mold App */}
      {isNoAccessPage ? (
        <a
          className="sidebar-brand d-flex align-items-center justify-content-center"
          href={navigationPaths.moldappLogin}
        >
          <div className="sidebar-brand-icon">
            <i className="fas fa-home"></i>
          </div>
        </a>
      ) : (
        <a
          className="sidebar-brand d-flex align-items-center justify-content-center"
          href={navigationPaths.moldAppMainMenu}
          target="_blank"
        >
          <div className="sidebar-brand-icon">
            <i className="fas fa-home"></i>
          </div>
        </a>
      )}

      {/* Divider */}
      <hr className="sidebar-divider" />

      {!isNoAccessPage && (
        <>
          {hasPermission("product_master.view_pisrequest") ? (
            <>
              <li className="nav-item">
                <a
                  className="nav-link collapsed"
                  href="#"
                  data-toggle="collapse"
                  data-target="#collapseMPN"
                  aria-expanded="false"
                  aria-controls="collapseMPN"
                >
                  <i className="fas fa-fw fa-sitemap"></i>
                  <span>MPN</span>
                </a>
                <div
                  id="collapseMPN"
                  className="collapse"
                  aria-labelledby="headingMPN"
                  data-parent="#accordionSidebar"
                >
                  <div className="bg-white py-2 collapse-inner rounded">
                    <h6 className="collapse-header">MPN</h6>

                    <NavLink
                      className="collapse-item"
                      to={navigationPaths.mpnIntegrations}
                      onClick={(event) =>
                        handleUnsavedChangesNavigation(
                          event,
                          navigationPaths.mpnIntegrations,
                        )
                      }
                      data-toggle="collapse"
                      data-target="#collapseMPN"
                      aria-expanded="true"
                      aria-controls="collapseMPN"
                    >
                      Dashboard
                    </NavLink>

                    {hasPermission("product_master.add_pisrequest") ? (
                      <NavLink
                        className="collapse-item"
                        to={navigationPaths.createNewMpn}
                        onClick={(event) =>
                          handleUnsavedChangesNavigation(
                            event,
                            navigationPaths.createNewMpn,
                            true,
                          )
                        }
                        data-toggle="collapse"
                        data-target="#collapseMPN"
                        aria-expanded="true"
                        aria-controls="collapseMPN"
                      >
                        Create new MPN
                      </NavLink>
                    ) : (
                      <></>
                    )}

                  <NavLink
                      className={isSearchINPage}
                      to={navigationPaths.searchIn}
                      onClick={(event) =>
                        handleUnsavedChangesNavigation(
                          event,
                          navigationPaths.productMasterSearch,
                        )
                      }
                      data-toggle="collapse"
                      data-target="#collapseMPN"
                      aria-expanded="true"
                      aria-controls="collapseMPN"
                    >
                      MPN Search
                    </NavLink>

                    
                   <NavLink
                      className={isSearchINPage}
                      to={navigationPaths.searchIn}
                      onClick={(event) =>
                        handleUnsavedChangesNavigation(
                          event,
                          navigationPaths.searchIn,
                        )
                      }
                      data-toggle="collapse"
                      data-target="#collapseMPN"
                      aria-expanded="true"
                      aria-controls="collapseMPN"
                    >
                      Search Item Number
                    </NavLink>


                    <NavLink
                      className="collapse-item"
                      to={navigationPaths.mpnRequestsList}
                      onClick={(event) =>
                        handleUnsavedChangesNavigation(
                          event,
                          navigationPaths.mpnRequestsList,
                        )
                      }
                      data-toggle="collapse"
                      data-target="#collapseMPN"
                      aria-expanded="true"
                      aria-controls="collapseMPN"
                    >
                      My approval requests
                    </NavLink>
                  </div>
                </div>
              </li>
              {/*  <li className="nav-item">*/}
              {/*  <a*/}
              {/*    className="nav-link collapsed"*/}
              {/*    href="#"*/}
              {/*    data-toggle="collapse"*/}
              {/*    data-target="#collapsePIS"*/}
              {/*    aria-expanded="false"*/}
              {/*    aria-controls="collapsePIS"*/}
              {/*  >*/}
              {/*    <i className="fas fa-fw fa-sitemap"></i>*/}
              {/*    <span>PIS</span>*/}
              {/*  </a>*/}
              {/*  <div*/}
              {/*    id="collapsePIS"*/}
              {/*    className="collapse"*/}
              {/*    aria-labelledby="headingPIS"*/}
              {/*    data-parent="#accordionSidebar"*/}
              {/*  >*/}
              {/*    <div className="bg-white py-2 collapse-inner rounded">*/}
              {/*      <h6 className="collapse-header">Product Information Sheet</h6>*/}
              {/*      {hasPermission("product_master.add_pisrequest") ? (*/}
              {/*        <NavLink*/}
              {/*          className="collapse-item"*/}
              {/*          to={navigationPaths.createNewPis}*/}
              {/*          onClick={(event) =>*/}
              {/*            handleUnsavedChangesNavigation(*/}
              {/*              event,*/}
              {/*              navigationPaths.createNewPis,*/}
              {/*              true,*/}
              {/*            )*/}
              {/*          }*/}
              {/*          data-toggle="collapse"*/}
              {/*          data-target="#collapsePIS"*/}
              {/*          aria-expanded="true"*/}
              {/*          aria-controls="collapsePIS"*/}
              {/*        >*/}
              {/*          Create new SKU*/}
              {/*        </NavLink>*/}
              {/*      ) : (*/}
              {/*        <></>*/}
              {/*      )}*/}
              {/*      <NavLink*/}
              {/*        className="collapse-item"*/}
              {/*        to={navigationPaths.pisRequestsList}*/}
              {/*        onClick={(event) =>*/}
              {/*          handleUnsavedChangesNavigation(*/}
              {/*            event,*/}
              {/*            navigationPaths.pisRequestsList,*/}
              {/*          )*/}
              {/*        }*/}
              {/*        data-toggle="collapse"*/}
              {/*        data-target="#collapsePIS"*/}
              {/*        aria-expanded="true"*/}
              {/*        aria-controls="collapsePIS"*/}
              {/*      >*/}
              {/*        My approval requests*/}
              {/*      </NavLink>*/}
              {/*    </div>*/}
              {/*  </div>*/}
              {/*</li>*/}
            </>
          ) : (
            <></>
          )}

          {/*{hasPermission("api.regional_approver_tcc_request") ||*/}
          {/*hasPermission("api.global_market_approver_tcc_request") ||*/}
          {/*hasPermission("api.global_approver_tcc_request") ||*/}
          {/*hasPermission("api.requester_tcc_request") ? (*/}
          {/*  <li className="nav-item">*/}
          {/*    <a*/}
          {/*      href="#"*/}
          {/*      className="nav-link collapsed"*/}
          {/*      data-toggle="collapse"*/}
          {/*      data-target="#collapseTCC"*/}
          {/*      aria-expanded="false"*/}
          {/*      aria-controls="collapseTCC"*/}
          {/*    >*/}
          {/*      <i className="fas fa-fw fa-poll-h"></i>*/}
          {/*      <span>TCC</span>*/}
          {/*    </a>*/}
          {/*    <div*/}
          {/*      id="collapseTCC"*/}
          {/*      className="collapse"*/}
          {/*      aria-labelledby="headingTCC"*/}
          {/*      data-parent="#accordionSidebar"*/}
          {/*    >*/}
          {/*      <div className="bg-white py-2 collapse-inner rounded">*/}
          {/*        <h6 className="collapse-header">Tup. Consumer Code</h6>*/}
          {/*        <NavLink*/}
          {/*          className={isSearchTCCPage}*/}
          {/*          to={navigationPaths.searchTcc}*/}
          {/*          onClick={(event) =>*/}
          {/*            handleUnsavedChangesNavigation(*/}
          {/*              event,*/}
          {/*              navigationPaths.searchTcc,*/}
          {/*            )*/}
          {/*          }*/}
          {/*          data-toggle="collapse"*/}
          {/*          data-target="#collapseTCC"*/}
          {/*          aria-expanded="true"*/}
          {/*          aria-controls="collapseTCC"*/}
          {/*        >*/}
          {/*          Search TCC*/}
          {/*        </NavLink>*/}
          {/*        <NavLink*/}
          {/*          className={isSearchTCCRequestPage}*/}
          {/*          to={navigationPaths.searchTccRequest}*/}
          {/*          onClick={(event) =>*/}
          {/*            handleUnsavedChangesNavigation(*/}
          {/*              event,*/}
          {/*              navigationPaths.searchTccRequest,*/}
          {/*            )*/}
          {/*          }*/}
          {/*          data-toggle="collapse"*/}
          {/*          data-target="#collapseTCC"*/}
          {/*          aria-expanded="true"*/}
          {/*          aria-controls="collapseTCC"*/}
          {/*        >*/}
          {/*          Search TCC Request*/}
          {/*        </NavLink>*/}
          {/*        <NavLink*/}
          {/*          className={isCreateTCCRequestPage}*/}
          {/*          to={""}*/}
          {/*          onClick={(event) =>*/}
          {/*            handleUnsavedChangesNavigation(*/}
          {/*              event,*/}
          {/*              navigationPaths.createNewTccRequest,*/}
          {/*            )*/}
          {/*          }*/}
          {/*          data-toggle="collapse"*/}
          {/*          data-target="#collapseTCC"*/}
          {/*          aria-expanded="true"*/}
          {/*          aria-controls="collapseTCC"*/}
          {/*        >*/}
          {/*          Create TCC Request*/}
          {/*        </NavLink>*/}
          {/*        {hasPermission("api.regional_approver_tcc_request") ||*/}
          {/*        hasPermission("api.global_market_approver_tcc_request") ||*/}
          {/*        hasPermission("api.global_approver_tcc_request") ? (*/}
          {/*          <NavLink*/}
          {/*            className={isMyTCCApprovalsPage}*/}
          {/*            to={navigationPaths.tccRequestApprovals}*/}
          {/*            onClick={(event) =>*/}
          {/*              handleUnsavedChangesNavigation(*/}
          {/*                event,*/}
          {/*                navigationPaths.tccRequestApprovals,*/}
          {/*              )*/}
          {/*            }*/}
          {/*            data-toggle="collapse"*/}
          {/*            data-target="#collapseTCC"*/}
          {/*            aria-expanded="true"*/}
          {/*            aria-controls="collapseTCC"*/}
          {/*          >*/}
          {/*            My TCC Approvals*/}
          {/*          </NavLink>*/}
          {/*        ) : (*/}
          {/*          <></>*/}
          {/*        )}*/}
          {/*      </div>*/}
          {/*    </div>*/}
          {/*  </li>*/}
          {/*) : (*/}
          {/*  <></>*/}
          {/*)}*/}

          {hasPermission("api.regional_approver_tcc_request") ||
          hasPermission("api.global_market_approver_tcc_request") ||
          hasPermission("api.global_approver_tcc_request") ||
          hasPermission("api.requester_tcc_request") ? (
            <li className="nav-item">
              <a
                href="#"
                className="nav-link collapsed"
                data-toggle="collapse"
                data-target="#collapseTCC"
                aria-expanded="false"
                aria-controls="collapseTCC"
              >
                <i className="fas fa-fw fa-poll-h"></i>
                <span>TCC</span>
              </a>

              <div
                id="collapseTCC"
                className="collapse"
                aria-labelledby="headingTCC"
                data-parent="#accordionSidebar"
              >
                <div className="bg-white py-2 collapse-inner rounded">
                  <h6 className="collapse-header">Tup. Consumer Code</h6>

                  <NavLink
                    className={isSearchTCCPage}
                    to={navigationPaths.searchTcc}
                    onClick={(event) =>
                      handleUnsavedChangesNavigation(
                        event,
                        navigationPaths.searchTcc,
                      )
                    }
                    data-toggle="collapse"
                    data-target="#collapseTCC"
                    aria-expanded="true"
                    aria-controls="collapseTCC"
                  >
                    Search TCC
                  </NavLink>

                  <NavLink
                    className={isSearchTCCRequestPage}
                    to={navigationPaths.searchTccRequest}
                    onClick={(event) =>
                      handleUnsavedChangesNavigation(
                        event,
                        navigationPaths.searchTccRequest,
                      )
                    }
                    data-toggle="collapse"
                    data-target="#collapseTCC"
                    aria-expanded="true"
                    aria-controls="collapseTCC"
                  >
                    Search TCC Request
                  </NavLink>

                  <NavLink
                    className={isCreateTCCRequestPage}
                    to={""}
                    onClick={(event) =>
                      handleUnsavedChangesNavigation(
                        event,
                        navigationPaths.createNewTccRequest,
                      )
                    }
                    data-toggle="collapse"
                    data-target="#collapseTCC"
                    aria-expanded="true"
                    aria-controls="collapseTCC"
                  >
                    Create TCC Request
                  </NavLink>

                  {hasPermission("api.regional_approver_tcc_request") ||
                  hasPermission("api.global_market_approver_tcc_request") ||
                  hasPermission("api.global_approver_tcc_request") ? (
                    <NavLink
                      className={isMyTCCApprovalsPage}
                      to={navigationPaths.tccRequestApprovals}
                      onClick={(event) =>
                        handleUnsavedChangesNavigation(
                          event,
                          navigationPaths.tccRequestApprovals,
                        )
                      }
                      data-toggle="collapse"
                      data-target="#collapseTCC"
                      aria-expanded="true"
                      aria-controls="collapseTCC"
                    >
                      My TCC Approvals
                    </NavLink>
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            </li>
          ) : (
            <></>
          )}

          {/*<ArtworkMasterMenu />*/}
          <ArtworkMenu />

          {/* Page Specific Navigation */}
          {isProdInfoRelatedPages && (
            <li className={prodInfoPageActiveLink}>
              <a
                className={`nav-link ${showMenu ? "" : "collapsed"}`}
                href="#"
                data-toggle="collapse"
                data-target="#collapsePIP"
                aria-expanded="true"
                aria-controls="collapsePIP"
              >
                <i className="fas fa-fw fa-book"></i>
                <span>Product Information</span>
              </a>
              <div
                id="collapsePIP"
                className={`collapse in ${showMenu ? "show" : ""}`}
                aria-labelledby="headingPIP"
                data-parent="#accordionSidebar"
              >
                <div className="bg-white py-2 collapse-inner rounded">
                  <NavLink
                    className="collapse-item"
                    to={navigationPaths.similarItems}
                    onClick={(event) =>
                      handleUnsavedChangesNavigation(
                        event,
                        navigationPaths.similarItems,
                      )
                    }
                    data-toggle="collapse"
                    data-target="#collapsePIP"
                    aria-expanded="true"
                    aria-controls="collapsePIP"
                  >
                    Similar Items
                  </NavLink>

                  <NavLink
                    className="collapse-item"
                    to={navigationPaths.buyersRelatedData}
                    onClick={(event) =>
                      handleUnsavedChangesNavigation(
                        event,
                        navigationPaths.buyersRelatedData,
                      )
                    }
                    data-toggle="collapse"
                    data-target="#collapsePIP"
                    aria-expanded="true"
                    aria-controls="collapsePIP"
                  >
                    Buyer's Related Data
                  </NavLink>
                </div>
              </div>
            </li>
          )}

          {/* Global Navigation */}
          {/* Nav Item - Product Master Search */}
          {/* <li className={prodMasterSearchActiveLink}>
            <NavLink
              className="nav-link"
              to={navigationPaths.productMasterSearch}
              onClick={(event) =>
                handleUnsavedChangesNavigation(
                  event,
                  navigationPaths.productMasterSearch,
                )
              }
            >
              <i className="fas fa-fw fa-search"></i>
              <span>Product Master Search</span>
            </NavLink>
          </li> */}

          {/* Nav Item - Global Product Hierarchy */}
          {hasPermission("api.view_datacatalog") && (
            <li className="nav-item">
              <a
                className="nav-link"
                target="_blank"
                href={navigationPaths.globalProductHierarchy}
              >
                <i className="fas fa-fw fa-database"></i>
                <span>Global Product Hierarchy</span>
              </a>
            </li>
          )}

          {/* Nav Item - Product Mass Update */}
          {hasPermission("api.view_pmuqueue") && (
            <li className="nav-item">
              <a
                className="nav-link"
                target="_blank"
                href={navigationPaths.productMassUpdate}
              >
                <FontAwesomeIcon icon={faFileArrowUp} className="fa fa-fw" />
                <span>Product Mass Update</span>
              </a>
            </li>
          )}

          {/* Nav Item - Packaging Procedures */}
          <li className="nav-item">
            <a
              className="nav-link"
              target="_blank"
              href={navigationPaths.packagingProcedures}
            >
              <i className="fas fa-fw fa-boxes"></i>
              <span>Packaging Procedures</span>
            </a>
          </li>

          {/* Nav Item - Bulks Search Tool */}
          <li className="nav-item">
            <a
              className="nav-link"
              target="_blank"
              href={navigationPaths.bulksSearchTool}
            >
              <i className="fas fa-fw fa-search"></i>
              <span>Bulks Search Tool</span>
            </a>
          </li>
          {/* Divider */}
          <hr className="sidebar-divider d-none d-md-block" />

          {/* Sidebar Toggler */}
          <div className="text-center d-none d-md-inline">
            <button
              className="rounded-circle border-0"
              onClick={toggle}
              id="sidebarToggle"
            ></button>
          </div>
        </>
      )}

      <VersionHud showMenu={showMenu}/>
    </ul>
  );
}

export default SideNav;
